// src/systems/wheel.ts
//
// Phase 3 — European single-zero wheel. Pure module: types-only deps.
// All constants frozen; slotByNumber backed by O(1) Map.
//
// Phase 13 (WHEEL-06, WHEEL-08, WHEEL-12): added composeWheel (deterministic
// 37-slot composer from numbersDeck + voidPositions) and isLosingSlot
// (centralised loss decider replacing scattered value===0 checks).
// WHEEL constant rebuilt with new discriminated-union Slot shape.

import type { Slot, SlotNumber, Color, BetType } from '../state/types';
import { shuffle } from './rng';
import type { Rng } from './rng';

// European single-zero wheel order (clockwise from 0).
export const WHEEL_ORDER: readonly SlotNumber[] = Object.freeze([
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10,
  5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26,
]) as readonly SlotNumber[];

export const RED_NUMBERS: ReadonlySet<SlotNumber> = new Set<SlotNumber>([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);

export function colorOf(n: SlotNumber): Color {
  if (n === 0) return 'green';
  return RED_NUMBERS.has(n) ? 'red' : 'black';
}

// WHEEL rebuilt with Phase 13 discriminated-union Slot shape.
// Legacy consumers that read slot.number must be updated to slot.value
// after narrowing on slot.kind === 'number' (see plans 13-03/13-04/13-05).
export const WHEEL: readonly Slot[] = Object.freeze(
  WHEEL_ORDER.map((n, position) => ({
    kind: 'number' as const,
    value: n,
    color: colorOf(n),
    position,
    modifier: null,
  })),
);

// SLOT_BY_NUMBER: keyed by value (the SlotNumber) on 'number'-kind slots.
const SLOT_BY_NUMBER: ReadonlyMap<SlotNumber, Slot> = new Map(
  (WHEEL as readonly (Slot & { kind: 'number' })[]).map((s) => [s.value, s]),
);

export function slotByNumber(n: SlotNumber): Slot {
  const s = SLOT_BY_NUMBER.get(n);
  if (!s) throw new Error(`Invalid slot number: ${n}`);
  return s;
}

// Uniform random slot pick (no bias). Ball effects modify this elsewhere.
export function pickSlot(rng: () => number): Slot {
  const idx = Math.floor(rng() * WHEEL.length);
  return WHEEL[idx]!;
}

/**
 * composeWheel(numbersDeck, voidPositions, rng)
 *
 * Deterministic 37-slot composer. Given the same numbersDeck, voidPositions,
 * and rng state, always produces the same output.
 *
 * Algorithm:
 *   1. Defensive: dedupe + clamp voidPositions to [0, 36] (T-13-02).
 *   2. Shuffle numbersDeck ONCE using rng (the ONLY rng draw per call — T-13-03).
 *   3. Walk positions 0..36:
 *      - If in voidSet → emit { kind:'void', position }.
 *      - If deck cursor < shuffled.length → emit { kind:'number', value, color, position }.
 *      - Otherwise (deck exhausted) → emit { kind:'void', position }.
 *
 * Per PITFALLS Pitfall 2: composeWheel takes Rng and consumes exactly one
 * shuffle (37 draws) — callers must pass a cloned Rng when determinism matters.
 */
export function composeWheel(
  numbersDeck: readonly SlotNumber[],
  voidPositions: readonly number[],
  rng: Rng,
): Slot[] {
  // T-13-02: dedupe + filter to valid wheel positions [0, 36].
  const voidSet = new Set(voidPositions.filter((p) => p >= 0 && p < 37));

  // Shuffle the numbers deck ONCE deterministically.
  const shuffled = shuffle(rng, numbersDeck);
  let deckCursor = 0;

  const out: Slot[] = [];
  for (let position = 0; position < 37; position++) {
    if (voidSet.has(position)) {
      out.push({ kind: 'void', position, modifier: null });
      continue;
    }
    if (deckCursor < shuffled.length) {
      const value = shuffled[deckCursor++]!;
      out.push({
        kind: 'number',
        value,
        color: colorOf(value),
        position,
        modifier: null,
      });
    } else {
      // Deck exhausted — remaining non-void positions become void slots.
      out.push({ kind: 'void', position, modifier: null });
    }
  }
  return out;
}

/**
 * isLosingSlot(slot, bet)
 *
 * Single source of truth for "this slot loses this bet" (WHEEL-12, BETS-09).
 * Replaces scattered `value === 0` checks in bets.ts / scoring.ts (done in plan 13-03).
 *
 * Rules:
 *   - Void slots lose ALL bets — no exceptions (void cells do not trigger safety_net).
 *   - Number 0 loses everything EXCEPT a straight-on-0 (v1 invariant retained).
 *   - Any other number slot is not a loss (the bet-resolution layer decides the hit).
 */
export function isLosingSlot(slot: Slot, bet: BetType): boolean {
  // Void slots lose ALL bets — no exceptions (BETS-09).
  if (slot.kind === 'void') return true;
  // Number slot: 0 loses everything except a straight-on-0.
  // (V1 invariant retained verbatim — see bets.ts original site.)
  if (slot.value === 0 && bet.kind !== 'straight') return true;
  return false;
}
